import CloseIcon from '@mui/icons-material/Close';
import { Box, Card, IconButton, Paper, Typography } from '@mui/material';
import React from 'react';
import { StyledButton } from '../../styles/StyledButtons';
import DocumentUploadProgressCard from './DocumentUploadProgressCard';
import { UploadedDocument, UploadProgress } from './types';
import {
  countCompletedUploads,
  hasWordDocumentsInUploads,
  shouldShowProcessDocumentsButton,
} from './utils';

interface FundOpportunityDocumentUploadPanelProps {
  uploadProgress: Map<string, UploadProgress>;
  uploadedDocuments: UploadedDocument[];
  isProcessing: boolean;
  onRemoveDocument: (fileName: string) => void;
  onProcessDocuments: () => void;
}

const FundOpportunityDocumentUploadPanel: React.FC<FundOpportunityDocumentUploadPanelProps> = ({
  uploadProgress,
  uploadedDocuments,
  isProcessing,
  onRemoveDocument,
  onProcessDocuments,
}) => {
  if (uploadProgress.size === 0 && uploadedDocuments.length === 0) {
    return null;
  }

  const showProcessButton = shouldShowProcessDocumentsButton(
    uploadProgress,
    uploadedDocuments,
    isProcessing,
  );

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Paper
        sx={{
          p: 2,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Documents ({countCompletedUploads(uploadProgress, uploadedDocuments)})
        </Typography>

        {hasWordDocumentsInUploads(uploadProgress, uploadedDocuments) && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              backgroundColor: 'warning.light',
              border: '1px solid',
              borderColor: 'warning.main',
              borderRadius: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'warning.dark',
                fontSize: '0.875rem',
              }}
            >
              ⚠️ Word documents (.doc/.docx) will be converted to PDF. For best accuracy, consider
              converting to PDF in Word and upload the PDF instead.
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mb: 2,
          }}
        >
          {Array.from(uploadProgress.values())
            .sort((a, b) => a.order - b.order)
            .map((progress) => (
              <DocumentUploadProgressCard
                key={progress.fileName}
                progress={progress}
                onRemove={onRemoveDocument}
              />
            ))}

          {uploadedDocuments
            .filter(
              (document) =>
                !Array.from(uploadProgress.values()).some(
                  (progress) => progress.fileName === document.name,
                ),
            )
            .map((document) => (
              <Card
                key={document.id}
                sx={{
                  minWidth: 100,
                  maxWidth: 180,
                  width: 'calc(20% - 8px)',
                  border: '1px solid',
                  borderColor: 'success.main',
                  backgroundColor: 'success.light',
                  padding: '0.5rem 0.75rem',
                  minHeight: '2.8125rem',
                  boxSizing: 'border-box',
                  '& .MuiCardContent-root': {
                    padding: '0 !important',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      backgroundColor: 'success.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '0.625rem',
                      minWidth: 20,
                      flexShrink: 0,
                    }}
                  >
                    ✓
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.75rem',
                        lineHeight: 1.2,
                        display: 'block',
                      }}
                    >
                      {document.name}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => onRemoveDocument(document.name)}
                    sx={{
                      p: 0.25,
                      minWidth: 20,
                      width: 20,
                      height: 20,
                      flexShrink: 0,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: '0.875rem' }} />
                  </IconButton>
                </Box>
              </Card>
            ))}
        </Box>

        {showProcessButton && (
          <StyledButton onClick={onProcessDocuments} disabled={isProcessing} noTopSpacing>
            {isProcessing ? 'Processing Documents...' : 'Process Documents'}
          </StyledButton>
        )}
      </Paper>
    </Box>
  );
};

export default FundOpportunityDocumentUploadPanel;
